/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  DeleteConnectorCredentialPathParameters,
  ListCredentialsForCurrentUserParams,
  TLConnectorCredentialResponse,
  TLCredentialListResponse,
  TLCredentialSchemaListResponse,
  TLDataConnectorsCredentialCreateRequest,
  TLDataConnectorsCredentialUpdateRequest,
  TLHTTPValidationError,
  TLSuccessResponse,
  UpdateConnectorCredentialPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Create Connector Credential
 */
export const createConnectorCredential = (
  tLDataConnectorsCredentialCreateRequest: BodyType<TLDataConnectorsCredentialCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConnectorCredentialResponse>(
    {
      url: `/credentials`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLDataConnectorsCredentialCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateConnectorCredentialQueryKey = (
  tLDataConnectorsCredentialCreateRequest?: BodyType<TLDataConnectorsCredentialCreateRequest>,
) => {
  return ['POST', 'credentials', tLDataConnectorsCredentialCreateRequest] as const;
};

export const getCreateConnectorCredentialQueryOptions = <
  TData = Awaited<ReturnType<typeof createConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataConnectorsCredentialCreateRequest: BodyType<TLDataConnectorsCredentialCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnectorCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getCreateConnectorCredentialQueryKey(tLDataConnectorsCredentialCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createConnectorCredential>>> = ({ signal }) =>
    createConnectorCredential(tLDataConnectorsCredentialCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createConnectorCredential>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateConnectorCredentialQueryResult = NonNullable<Awaited<ReturnType<typeof createConnectorCredential>>>;
export type CreateConnectorCredentialQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateConnectorCredential<
  TData = Awaited<ReturnType<typeof createConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataConnectorsCredentialCreateRequest: BodyType<TLDataConnectorsCredentialCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnectorCredential>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createConnectorCredential>>,
          TError,
          Awaited<ReturnType<typeof createConnectorCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateConnectorCredential<
  TData = Awaited<ReturnType<typeof createConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataConnectorsCredentialCreateRequest: BodyType<TLDataConnectorsCredentialCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnectorCredential>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createConnectorCredential>>,
          TError,
          Awaited<ReturnType<typeof createConnectorCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateConnectorCredential<
  TData = Awaited<ReturnType<typeof createConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataConnectorsCredentialCreateRequest: BodyType<TLDataConnectorsCredentialCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnectorCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Connector Credential
 */

export function useCreateConnectorCredential<
  TData = Awaited<ReturnType<typeof createConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataConnectorsCredentialCreateRequest: BodyType<TLDataConnectorsCredentialCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnectorCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateConnectorCredentialQueryOptions(tLDataConnectorsCredentialCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Connector Credential
 */
export const prefetchCreateConnectorCredentialQuery = async <
  TData = Awaited<ReturnType<typeof createConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLDataConnectorsCredentialCreateRequest: BodyType<TLDataConnectorsCredentialCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConnectorCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateConnectorCredentialQueryOptions(tLDataConnectorsCredentialCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Connector Credential
 */
export const invalidateCreateConnectorCredential = async (
  queryClient: QueryClient,
  tLDataConnectorsCredentialCreateRequest: BodyType<TLDataConnectorsCredentialCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateConnectorCredentialQueryKey(tLDataConnectorsCredentialCreateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary List Credentials For Current User
 */
export const listCredentialsForCurrentUser = (
  params?: ListCredentialsForCurrentUserParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCredentialListResponse>({ url: `/credentials/me`, method: 'GET', params, signal }, options);
};

export const getListCredentialsForCurrentUserQueryKey = (params?: ListCredentialsForCurrentUserParams) => {
  return ['credentials', 'me', ...(params ? [params] : [])] as const;
};

export const getListCredentialsForCurrentUserQueryOptions = <
  TData = Awaited<ReturnType<typeof listCredentialsForCurrentUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListCredentialsForCurrentUserParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCredentialsForCurrentUser>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCredentialsForCurrentUserQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCredentialsForCurrentUser>>> = ({ signal }) =>
    listCredentialsForCurrentUser(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listCredentialsForCurrentUser>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListCredentialsForCurrentUserQueryResult = NonNullable<
  Awaited<ReturnType<typeof listCredentialsForCurrentUser>>
>;
export type ListCredentialsForCurrentUserQueryError = ErrorType<TLHTTPValidationError>;

export function useListCredentialsForCurrentUser<
  TData = Awaited<ReturnType<typeof listCredentialsForCurrentUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListCredentialsForCurrentUserParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCredentialsForCurrentUser>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCredentialsForCurrentUser>>,
          TError,
          Awaited<ReturnType<typeof listCredentialsForCurrentUser>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCredentialsForCurrentUser<
  TData = Awaited<ReturnType<typeof listCredentialsForCurrentUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListCredentialsForCurrentUserParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCredentialsForCurrentUser>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCredentialsForCurrentUser>>,
          TError,
          Awaited<ReturnType<typeof listCredentialsForCurrentUser>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCredentialsForCurrentUser<
  TData = Awaited<ReturnType<typeof listCredentialsForCurrentUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListCredentialsForCurrentUserParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCredentialsForCurrentUser>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Credentials For Current User
 */

export function useListCredentialsForCurrentUser<
  TData = Awaited<ReturnType<typeof listCredentialsForCurrentUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListCredentialsForCurrentUserParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCredentialsForCurrentUser>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListCredentialsForCurrentUserQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Credentials For Current User
 */
export const prefetchListCredentialsForCurrentUserQuery = async <
  TData = Awaited<ReturnType<typeof listCredentialsForCurrentUser>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListCredentialsForCurrentUserParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCredentialsForCurrentUser>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListCredentialsForCurrentUserQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Credentials For Current User
 */
export const invalidateListCredentialsForCurrentUser = async (
  queryClient: QueryClient,
  params?: ListCredentialsForCurrentUserParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListCredentialsForCurrentUserQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Get Credential Schemas
 */
export const getCredentialSchemas = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLCredentialSchemaListResponse>({ url: `/credentials/schema`, method: 'GET', signal }, options);
};

export const getGetCredentialSchemasQueryKey = () => {
  return ['credentials', 'schema'] as const;
};

export const getGetCredentialSchemasQueryOptions = <
  TData = Awaited<ReturnType<typeof getCredentialSchemas>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialSchemas>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCredentialSchemasQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCredentialSchemas>>> = ({ signal }) =>
    getCredentialSchemas(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCredentialSchemas>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetCredentialSchemasQueryResult = NonNullable<Awaited<ReturnType<typeof getCredentialSchemas>>>;
export type GetCredentialSchemasQueryError = ErrorType<unknown>;

export function useGetCredentialSchemas<
  TData = Awaited<ReturnType<typeof getCredentialSchemas>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialSchemas>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCredentialSchemas>>,
          TError,
          Awaited<ReturnType<typeof getCredentialSchemas>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCredentialSchemas<
  TData = Awaited<ReturnType<typeof getCredentialSchemas>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialSchemas>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCredentialSchemas>>,
          TError,
          Awaited<ReturnType<typeof getCredentialSchemas>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCredentialSchemas<
  TData = Awaited<ReturnType<typeof getCredentialSchemas>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialSchemas>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Credential Schemas
 */

export function useGetCredentialSchemas<
  TData = Awaited<ReturnType<typeof getCredentialSchemas>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialSchemas>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCredentialSchemasQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Credential Schemas
 */
export const prefetchGetCredentialSchemasQuery = async <
  TData = Awaited<ReturnType<typeof getCredentialSchemas>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCredentialSchemas>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetCredentialSchemasQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Credential Schemas
 */
export const invalidateGetCredentialSchemas = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetCredentialSchemasQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Delete Connector Credential
 */
export const deleteConnectorCredential = (
  { credentialId }: DeleteConnectorCredentialPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>({ url: `/credentials/${credentialId}`, method: 'DELETE', signal }, options);
};

export const getDeleteConnectorCredentialQueryKey = ({ credentialId }: DeleteConnectorCredentialPathParameters) => {
  return ['DELETE', 'credentials', credentialId] as const;
};

export const getDeleteConnectorCredentialQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: DeleteConnectorCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnectorCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteConnectorCredentialQueryKey({ credentialId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteConnectorCredential>>> = ({ signal }) =>
    deleteConnectorCredential({ credentialId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: credentialId !== null && credentialId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteConnectorCredential>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteConnectorCredentialQueryResult = NonNullable<Awaited<ReturnType<typeof deleteConnectorCredential>>>;
export type DeleteConnectorCredentialQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteConnectorCredential<
  TData = Awaited<ReturnType<typeof deleteConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteConnectorCredentialPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnectorCredential>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteConnectorCredential>>,
          TError,
          Awaited<ReturnType<typeof deleteConnectorCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteConnectorCredential<
  TData = Awaited<ReturnType<typeof deleteConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteConnectorCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnectorCredential>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteConnectorCredential>>,
          TError,
          Awaited<ReturnType<typeof deleteConnectorCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteConnectorCredential<
  TData = Awaited<ReturnType<typeof deleteConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteConnectorCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnectorCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Connector Credential
 */

export function useDeleteConnectorCredential<
  TData = Awaited<ReturnType<typeof deleteConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: DeleteConnectorCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnectorCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteConnectorCredentialQueryOptions({ credentialId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Connector Credential
 */
export const prefetchDeleteConnectorCredentialQuery = async <
  TData = Awaited<ReturnType<typeof deleteConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { credentialId }: DeleteConnectorCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConnectorCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteConnectorCredentialQueryOptions({ credentialId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Connector Credential
 */
export const invalidateDeleteConnectorCredential = async (
  queryClient: QueryClient,
  { credentialId }: DeleteConnectorCredentialPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteConnectorCredentialQueryKey({ credentialId }) }, options);

  return queryClient;
};

/**
 * @summary Update Connector Credential
 */
export const updateConnectorCredential = (
  { credentialId }: UpdateConnectorCredentialPathParameters,
  tLDataConnectorsCredentialUpdateRequest: BodyType<TLDataConnectorsCredentialUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConnectorCredentialResponse>(
    {
      url: `/credentials/${credentialId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLDataConnectorsCredentialUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateConnectorCredentialQueryKey = (
  { credentialId }: UpdateConnectorCredentialPathParameters,
  tLDataConnectorsCredentialUpdateRequest?: BodyType<TLDataConnectorsCredentialUpdateRequest>,
) => {
  return ['PATCH', 'credentials', credentialId, tLDataConnectorsCredentialUpdateRequest] as const;
};

export const getUpdateConnectorCredentialQueryOptions = <
  TData = Awaited<ReturnType<typeof updateConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: UpdateConnectorCredentialPathParameters,
  tLDataConnectorsCredentialUpdateRequest: BodyType<TLDataConnectorsCredentialUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnectorCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getUpdateConnectorCredentialQueryKey({ credentialId }, tLDataConnectorsCredentialUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateConnectorCredential>>> = ({ signal }) =>
    updateConnectorCredential({ credentialId }, tLDataConnectorsCredentialUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: credentialId !== null && credentialId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateConnectorCredential>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateConnectorCredentialQueryResult = NonNullable<Awaited<ReturnType<typeof updateConnectorCredential>>>;
export type UpdateConnectorCredentialQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateConnectorCredential<
  TData = Awaited<ReturnType<typeof updateConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateConnectorCredentialPathParameters,
  tLDataConnectorsCredentialUpdateRequest: BodyType<TLDataConnectorsCredentialUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnectorCredential>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateConnectorCredential>>,
          TError,
          Awaited<ReturnType<typeof updateConnectorCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateConnectorCredential<
  TData = Awaited<ReturnType<typeof updateConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateConnectorCredentialPathParameters,
  tLDataConnectorsCredentialUpdateRequest: BodyType<TLDataConnectorsCredentialUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnectorCredential>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateConnectorCredential>>,
          TError,
          Awaited<ReturnType<typeof updateConnectorCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateConnectorCredential<
  TData = Awaited<ReturnType<typeof updateConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateConnectorCredentialPathParameters,
  tLDataConnectorsCredentialUpdateRequest: BodyType<TLDataConnectorsCredentialUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnectorCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Connector Credential
 */

export function useUpdateConnectorCredential<
  TData = Awaited<ReturnType<typeof updateConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { credentialId }: UpdateConnectorCredentialPathParameters,
  tLDataConnectorsCredentialUpdateRequest: BodyType<TLDataConnectorsCredentialUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnectorCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateConnectorCredentialQueryOptions(
    { credentialId },
    tLDataConnectorsCredentialUpdateRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Connector Credential
 */
export const prefetchUpdateConnectorCredentialQuery = async <
  TData = Awaited<ReturnType<typeof updateConnectorCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { credentialId }: UpdateConnectorCredentialPathParameters,
  tLDataConnectorsCredentialUpdateRequest: BodyType<TLDataConnectorsCredentialUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConnectorCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateConnectorCredentialQueryOptions(
    { credentialId },
    tLDataConnectorsCredentialUpdateRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Connector Credential
 */
export const invalidateUpdateConnectorCredential = async (
  queryClient: QueryClient,
  { credentialId }: UpdateConnectorCredentialPathParameters,
  tLDataConnectorsCredentialUpdateRequest: BodyType<TLDataConnectorsCredentialUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateConnectorCredentialQueryKey({ credentialId }, tLDataConnectorsCredentialUpdateRequest) },
    options,
  );

  return queryClient;
};
